import { createRouter, createWebHistory } from 'vue-router';

import store from '@/store';
import { hasBrowser, isHydrating } from '@/utils/env';
import {getEntityConfigs} from "@/entityConfigs";
import * as openalexId from "@/openalexId";
import {url} from "@/url";
import {shouldAwaitLiveUser} from "@/store/userBoot";

// Route views (oxjob #860, bundle diet): everything except the hot paths
// (home, SERP, entity page, auth pages, 404) is a lazy `() => import()` so it
// lands in its own chunk instead of app.js. The eager set is what a cold
// visitor is most likely to hit first — keeping those in app.js avoids a
// serial chunk fetch before the first route can resolve (the top bar is
// gated on route resolution, see App.vue).
import HomeV2Page from '@/views/HomeV2.vue'  // #681 landing revision — SHIPPED to / 2026-08-12 (old page: views/Home.vue, unrouted)
import SerpPage from '@/views/Serp.vue';
import EntityPage from '@/views/EntityPage.vue';

import LoginPage from '@/views/Login.vue';
import OAuthConsentPage from '@/views/OAuthConsent.vue';
import SignupPage from "@/views/Signup.vue";
import UserMagicToken from '@/components/User/UserMagicToken.vue';
import UserVerifyEmail from '@/components/User/UserVerifyEmail.vue';

const AboutPage = () => import('@/views/About.vue');
const TestimonialsPage = () => import('@/views/Testimonials.vue');
const TeamPage = () => import('@/views/Team.vue');
const BrandPage = () => import('@/views/Brand.vue');
const OqlPlayground = () => import('@/views/OqlPlayground.vue');
const NlWorkbench = () => import('@/views/NlWorkbench.vue');
const QuestionMap = () => import('@/views/QuestionMap.vue');
const Funders2026Page = () => import('@/views/Funders2026.vue');
const Paris2026Page = () => import('@/views/Paris2026.vue');
const PricingPage = () => import('@/views/PricingPage.vue');
const PricingPageNew = () => import('@/views/PricingPageNew.vue');
const MembersPage = () => import('@/views/MembersPage.vue');

const MeAbout = () => import('@/views/Me/MeProfile.vue');
const MeSearches = () => import('@/views/Me/MeSearches.vue');
const MeAlerts = () => import('@/views/Me/MeAlerts.vue');
const MeExports = () => import('@/views/Me/MeExports.vue');
const MePlan = () => import('@/views/Me/MePlan.vue');
const MeApiKey = () => import('@/views/Me/MeApiKey.vue');
const MeCollections = () => import('@/views/Me/MeCollections.vue');
const MeAppearance = () => import('@/views/Me/MeAppearance.vue');

const SettingsBase = () => import('@/views/Settings/SettingsBase.vue');
const SettingsLabs = () => import('@/views/Settings/SettingsLabs.vue');
const SettingsOrgProfile = () => import('@/views/Settings/SettingsOrgProfile.vue');
const SettingsOrgPlan = () => import('@/views/Settings/SettingsOrgPlan.vue');
const SettingsOrgUsage = () => import('@/views/Settings/SettingsOrgUsage.vue');
const SettingsOrgMembers = () => import('@/views/Settings/SettingsOrgMembers.vue');
const SettingsAffiliations = () => import('@/views/Settings/SettingsAffiliations.vue');
const SettingsOrgAdvisoryBoard = () => import('@/views/Settings/SettingsOrgAdvisoryBoard.vue');
const SettingsOrgUnsub = () => import('@/views/Settings/SettingsOrgUnsub.vue');
const SettingsOrgMeetings = () => import('@/views/Settings/SettingsOrgMeetings.vue');

const SettingsSiteAffiliations = () => import('@/views/Settings/SettingsSiteAffiliations.vue');

import PageNotFound from "@/views/PageNotFound.vue";
const AdminBase = () => import('@/views/Admin/AdminBase.vue');
const AdminUsers = () => import('@/views/Admin/AdminUsers.vue');
const AdminHiring = () => import('@/views/Admin/AdminHiring.vue');
// import AdminExports from "@/views/Admin/AdminExports.vue";
const AdminUserDetail = () => import('@/views/Admin/AdminUserDetail.vue');
const AdminOrganizations = () => import('@/views/Admin/AdminOrganizations.vue');
const AdminOrganizationDetail = () => import('@/views/Admin/AdminOrganizationDetail.vue');
const AdminOrganizationMembers = () => import('@/views/Admin/AdminOrganizationMembers.vue');
const AdminPlans = () => import('@/views/Admin/AdminPlans.vue');
// import AdminApiKeys from "@/views/Admin/AdminApiKeys.vue";
// import AdminMultipleApiKeys from "@/views/Admin/AdminMultipleApiKeys.vue";
const AdminAffiliations = () => import('@/views/Admin/AdminAffiliations.vue');
const AdminCurations = () => import('@/views/Admin/AdminCurations.vue');
const AdminFeatureFlags = () => import('@/views/Admin/AdminExperimental.vue');
const AdminCollections = () => import('@/views/Admin/AdminCollections.vue');



// TOOD Check if these are equal
//const entityNames = Object.keys(getConfigs()).join("|");
const entityNames = getEntityConfigs()
    .filter(c => c.hasSerp !== false) // Exclude entities without SERP pages (like locations)
    .map(c => c.name)
    .join("|")

const redirect = (path, url) => ({
    path,
    name: `redirect-${path.replace(/[^a-zA-Z0-9]/g, '-')}`,
    component: {
        created() {
            window.location.href = url;
        },
        template: '<div>Redirecting...</div>'
    }
});

// Contextual chrome (oxjob #778, #853): `meta.chrome` picks the page's chrome in App.vue.
//   'site' / (unset, defaults to 'app') — one scrolling SiteTopBar + SiteFooter
//   'bare' — auth pages: centered card only, no chrome
// 'site' and 'app' now render the SAME top bar; the label is kept only where a
// route still branches on it (e.g. hiding the top bar's "Open app" button in-app).
export const routes = [
    {
        // #681 landing-page revision, shipped 2026-08-12. Keeps name 'Home' so every
        // {name:'Home'} redirect/link in the app still works.
        path: '/',
        component: HomeV2Page,
        name: 'Home',
        meta: {chrome: 'site'},
    },
    {
        // legacy preview URL for the #681 build — shared internally, keep it working
        path: '/v2',
        redirect: '/',
    },
    // data pages
    {
        // Entity-less query namespace. OQL is self-describing (the entity lives in
        // the query, e.g. `works where …`), so OQL queries land here as `/q?oql=…`
        // rather than on the entity-typed `/:entityType` route, where the path
        // entity could disagree with the OQL. OQL is the ONLY query dialect accepted
        // here — OQO is an API-only surface, never a GUI URL input. This `/q`
        // namespace is also the future home for opaque short links (`/q/:shortid`,
        // oxjob #464). (oxjob #373 Phase 2; OQLO charter decision 25)
        path: '/q',
        name: 'OqlQuery',
        component: SerpPage,
    },
    {
        path: `/:entityType(${entityNames})`,
        name: 'Serp',
        component: SerpPage,
        beforeEnter: (to) => {
            // Back-compat: already-shared `/:entityType?oql=…` links (and any with a
            // deliberately MISMATCHED path entity, e.g. `/authors?oql=works where…`)
            // redirect to the entity-less `/q?oql=…`. Dropping the path entity makes
            // the path-vs-OQL mismatch structurally impossible — the OQL is the sole
            // source of the entity. Preserve the rest of the query (mode, per_page,
            // …). (oxjob #373 Phase 2)
            // (#1689: guards return their result; vue-router 5 deprecates next().)
            if (to.query.oql) {
                return { name: 'OqlQuery', query: to.query };
            }
        },
    },
    {
        path: '/locations/:entityId(.*)',
        name: 'LocationPage',
        component: EntityPage,
        // entityType is set in the global guard below (#1689: see entityRouteFixup).
    },
    {
        path: `/:entityId([waspfictg]\\d+)`,
        name: 'EntityPageShortcut',
        redirect: to => {
            const entityType = openalexId.getEntityType(to.params.entityId)
            console.log("routes EntityPageShortcut", to.params)
            return {
                name: "EntityPage",
                params: {
                    entityType,
                    entityId: to.params.entityId,
                },
            }
        }
    },
    {
        path: `/:entityType(${entityNames})/:entityId`,
        name: 'EntityPage',
        component: EntityPage,
        // Namespaced ids are normalized in the global guard below (#1689: see entityRouteFixup).
    },

    // user pages and routes
    {path: '/signup', name: 'Signup', component: SignupPage, meta: {chrome: 'bare'}},
    {path: '/login', name: 'Login', component: LoginPage, meta: {chrome: 'bare'}},
    // OAuth consent for the OpenAlex MCP server (oxjob #1266); requiresAuth bounces through /login and back.
    {path: '/oauth/consent', name: 'OAuthConsent', component: OAuthConsentPage, meta: {chrome: 'bare', requiresAuth: true}},
    // Password sign-in for the OpenAI plugin reviewer account only (oxjob #1294); unlinked.
    {path: '/login/reviewer', name: 'ReviewerLogin', component: () => import('@/views/ReviewerLogin.vue'), meta: {chrome: 'bare'}},
    {path: '/login/magic-token/:token', name: 'Magic-token', component: UserMagicToken, meta: {chrome: 'bare'}},
    {path: '/verify-email', name: 'VerifyEmail', component: UserVerifyEmail, meta: {chrome: 'bare'}},
    // ORCID sign-in return for profile claims (oxjob #1466).
    {path: '/orcid-callback', name: 'OrcidCallback', component: () => import('@/views/OrcidCallback.vue'), meta: {requiresAuth: true}},
    // Legacy route - redirect old password reset links to login
    {path: '/reset-password', redirect: { name: 'Login' }},

    // Account Pages - redirect old /me routes to /settings
    {
        path: '/me',
        redirect: '/settings/profile',
    },
    {
        path: '/me/about',
        redirect: '/settings/profile',
    },
    {
        path: '/me/api',
        redirect: '/settings/api-key',
    },
    {
        path: '/me/searches',
        redirect: '/settings/searches',
    },
    {
        path: '/me/exports',
        redirect: '/settings/exports',
    },
    {
        path: '/me/tags',
        redirect: '/settings/searches',
    },
    {
        path: '/me/organization',
        redirect: '/settings/org-profile',
    },
    {
        path: '/me/plan',
        redirect: '/settings/usage',
    },

    // Unified Settings Pages
    {
        path: '/settings',
        component: SettingsBase,
        meta: {requiresAuth: true},
        children: [
            {
                path: '',
                name: 'settings-home',
                redirect: '/settings/profile',
            },
            {
                path: 'profile',
                name: 'settings-profile',
                component: MeAbout,
            },
            {
                path: 'usage',
                name: 'settings-usage',
                component: MePlan,
            },
            {
                path: 'labs',
                name: 'settings-labs',
                component: SettingsLabs,
            },
            {
                path: 'plan',
                redirect: '/settings/usage',
            },
            {
                path: 'api-key',
                name: 'settings-api-key',
                component: MeApiKey,
            },
            {
                path: 'api',
                redirect: '/settings/api-key',
            },
            {
                path: 'searches',
                name: 'settings-searches',
                component: MeSearches,
            },
            {
                path: 'alerts',
                name: 'settings-alerts',
                component: MeAlerts,
            },
            {
                path: 'exports',
                name: 'settings-exports',
                component: MeExports,
            },
            {
                path: 'tickets',
                name: 'settings-tickets',
                component: () => import('@/views/Me/MeTickets.vue'),
            },
            {
                path: 'tickets/:ticketId',
                name: 'settings-ticket-detail',
                component: () => import('@/views/Me/MeTicketDetail.vue'),
                props: true,
            },
            {
                path: 'collections',
                name: 'settings-collections',
                component: MeCollections,
            },
            {
                path: 'appearance',
                name: 'settings-appearance',
                component: MeAppearance,
            },
            {
                path: 'curations',
                name: 'settings-curations',
                component: AdminCurations,
                meta: { requiresOrgCuratorOrOwner: true },
            },
            {
                path: 'curations/:curationId',
                name: 'settings-curation-detail',
                component: () => import('@/views/Admin/AdminCurationDetail.vue'),
                props: true,
                meta: { requiresOrgCuratorOrOwner: true },
            },
            {
                path: 'tags',
                redirect: '/settings/searches',
            },
            {
                path: 'org-profile',
                name: 'settings-org-profile',
                component: SettingsOrgProfile,
            },
            {
                path: 'org-api',
                name: 'settings-org-api',
                component: SettingsOrgUsage,
                meta: { requiresOrgOwner: true },
            },
            {
                path: 'org-usage',
                redirect: 'org-api',
            },
            {
                path: 'org-plan',
                name: 'settings-org-plan',
                component: SettingsOrgPlan,
                meta: { requiresOrgOwner: true },
            },
            {
                path: 'org-members',
                name: 'settings-org-members',
                component: SettingsOrgMembers,
                meta: { requiresOrgOwner: true },
            },
            {
                path: 'affiliations',
                name: 'settings-affiliations',
                component: SettingsAffiliations,
                meta: { requiresOrgCuratorOrOwner: true },
            },
            {
                // Readable by any org member; nomination/ballot actions are
                // owner-gated in-component and again server-side.
                path: 'advisory-board',
                name: 'settings-advisory-board',
                component: SettingsOrgAdvisoryBoard,
            },
            {
                path: 'unsub',
                name: 'settings-unsub',
                component: SettingsOrgUnsub,
                meta: { requiresOrgOwner: true },
            },
            {
                path: 'member-meetings',
                name: 'settings-member-meetings',
                component: SettingsOrgMeetings,
            },
            {
                path: 'site-affiliations',
                name: 'settings-site-affiliations',
                component: SettingsSiteAffiliations,
                meta: { requiresSiteWideAccess: true },
            },
            {
                path: 'site-curations',
                name: 'settings-site-curations',
                component: AdminCurations,
                meta: { requiresSiteWideAccess: true },
            },
            {
                path: 'site-curations/:curationId',
                name: 'settings-site-curation-detail',
                component: () => import('@/views/Admin/AdminCurationDetail.vue'),
                props: true,
                meta: { requiresSiteWideAccess: true },
            },
        ]
    },

    // CAB nominee confirm/decline landing page — target of the links in the
    // cab_nominee_confirmation email. Public: the emailed token is the auth.
    {path: '/cab/confirm', name: 'CabNominationConfirm', component: () => import('@/views/CabNominationConfirm.vue'), meta: {chrome: 'site'}},

    // The collections search page (oxjob #1532): public collections, plus your own.
    {path: '/collections', name: 'CollectionsSerp', component: () => import('@/views/CollectionsSerp.vue')},
    // Collection detail page (owner + admin only since QA-040). Backend pattern
    // is `col_<10-char-base58>` (oxjob #228 QA-042 / migration 047). Constrain
    // the route param so URL-encoded path-traversal (e.g. `foo%2F..%2Fadmin`)
    // doesn't even hit the component (security review M8).
    {path: '/collections/:collection_id([A-Za-z0-9_-]+)', name: 'CollectionPublic', component: () => import('@/views/CollectionPublic.vue')},

    // Repository Dashboard
    {path: '/repositories', name: 'Repositories', component: () => import('@/views/Repositories/RepositoriesLanding.vue')},
    {path: '/repositories/add', name: 'RepositoryAdd', component: () => import('@/views/Repositories/RepositoryAdd.vue')},
    // Legacy standalone repository dashboard (#83.7 era) — superseded by the
    // source page's Harvest tab (#836); users-api /repositories/<id> is retired (#804).
    {path: '/repositories/:sourceId', redirect: (to) => {
        const raw = String(to.params.sourceId).trim();
        const id = /^\d+$/.test(raw) ? `S${raw}` : raw.toUpperCase().startsWith('S') ? `S${raw.slice(1)}` : raw;
        return {path: `/sources/${id}`, query: {tab: 'harvest'}};
    }},

    // static pages
    {path: '/about', name: 'About', component: AboutPage, meta: {chrome: 'site'}},
    {path: '/projects', redirect: {name: "About"}},  // help.openalex.org "About us" links here (zd#8351)
    {path: '/team', name: 'Team', component: TeamPage, meta: {chrome: 'site'}},
    {path: '/transparency', redirect: {name: "About"}},  // transparency payload folded into /about (#685)
    {path: '/legal', redirect: '/terms'},
    {path: '/privacy', name: 'Privacy', component: () => import('@/views/Privacy.vue'), meta: {chrome: 'site'}},
    {path: '/terms', name: 'Terms', component: () => import('@/views/Terms.vue'), meta: {chrome: 'site'}},
    {path: '/brand', name: 'Brand', component: BrandPage, meta: {chrome: 'site'}},
    // The OQL doc pages that used to live under /query/oql/* (cheatsheet, guide, api,
    // spec, grammar, schema) moved to the help center (oxjob #778; content migrated in
    // #354/#750) — old links redirect to their help.openalex.org equivalents below.
    // The one survivor is the Cases browser: its corpus was never migrated, and the
    // help center links to it (access/oql, access/oql-spec).
    // The standalone Builder tab was REMOVED (oxjob #475): it was a second, divergent
    // copy of the SERP's OQL builder (it preserved value order while the real SERP path
    // alphabetized), which masked the reorder bug. The SERP advanced-mode builder is the
    // only one now — redirect any old `/query/oql/builder?oql=…` link there.
    {path: '/query/oql/builder', redirect: to => ({name: 'OqlQuery', query: {...to.query, mode: 'advanced'}})},
    {path: '/query/oql/cases', name: 'Query', component: OqlPlayground},
    {path: '/query/oql/cases/:id', name: 'QueryOqlCase', component: () => import('@/components/OqlPlayground/PlaygroundCaseDetail.vue'), props: true},
    redirect('/query', "https://help.openalex.org/access/oql"),
    redirect('/query/oql/cheatsheet', "https://help.openalex.org/access/oql"),
    redirect('/query/oql/guide', "https://help.openalex.org/access/oql"),
    redirect('/query/oql/playground', "https://help.openalex.org/access/oql"),  // editor removed in #530; docs now in help center
    redirect('/query/oql/api', "https://help.openalex.org/api/oql"),
    redirect('/query/oql/spec', "https://help.openalex.org/access/oql-spec"),
    redirect('/query/oql/grammar', "https://help.openalex.org/access/oql-spec"),
    redirect('/query/oql/schema', "https://help.openalex.org/access/oqo-schema"),
    // Natural-language tools, now at their own top-level /nl page (#630 Phase 2).
    {path: '/nl', redirect: '/nl/cases'},
    // The question map (#1618): every question OQL and the chat agent must answer, by need. Staff only; its data
    // (real users' questions) is fetched from oxjobs.org for admins, never bundled.
    {path: '/nl/map', name: 'QuestionMap', component: QuestionMap, meta: {requiresAuth: true, requiresAdmin: true, chrome: 'site'}},
    // One page now (Jason, 2026-10-10): an old need link opens the list filtered to that type.
    {path: '/nl/map/:need', redirect: to => ({path: '/nl/map', query: {need: to.params.need}})},
    {path: '/nl/:section(cases|annotate|playground)', name: 'Nl', component: NlWorkbench, props: true},
    // NL gold-standard annotator (#382): deep-link a specific case with it preselected.
    {path: '/nl/annotate/:id', name: 'NlAnnotate', component: NlWorkbench, props: route => ({section: 'annotate', caseId: route.params.id})},
    // Old /query/nl/* links → the new /nl page.
    {path: '/query/nl/annotate/:id', redirect: to => `/nl/annotate/${to.params.id}`},
    {path: '/query/nl/:section(cases|annotate|playground)', redirect: to => `/nl/${to.params.section}`},
    {path: '/query/nl', redirect: '/nl/cases'},
    // Legacy /oql-playground paths.
    redirect('/oql-playground', "https://help.openalex.org/access/oql"),
    {path: '/oql-playground/cases/:id', redirect: to => `/query/oql/cases/${to.params.id}`},
    {path: '/dev/pricing', name: 'Pricing', component: PricingPage, meta: {chrome: 'site'}},
    {path: '/dev/pricing-new', name: 'PricingNew', component: PricingPageNew, meta: {chrome: 'site'}},
    {path: '/pricing', name: 'PricingNewer', component: () => import('@/views/PricingPageNewer.vue'), meta: {chrome: 'site'}},
    {path: '/pricing/institutions', redirect: '/pricing#subscriptions'},
    {path: '/pricing/developers', redirect: '/pricing#subscriptions'},
    {path: '/pricing-newer', redirect: '/pricing'},
    {path: '/compare', name: 'Compare', component: () => import('@/views/ComingSoonPage.vue'), meta: {chrome: 'site'}, props: {title: 'Compare', blurb: "We're building a side-by-side look at how OpenAlex stacks up against other scholarly data sources. It's coming soon."}},
    // Jobs pitch page (oxjob #662/#792/#812): LIVE since the hiring launch swap. Was parked on
    // /jobs/draft during the stealth period; the old coming-soon placeholder is gone.
    {path: '/jobs', name: 'Jobs', component: () => import('@/views/JobsPage.vue'), meta: {chrome: 'site'}},
    // "jobs is shorter and less pretentious, but there's a redirect from /careers to /jobs" (Jason)
    {path: '/careers', redirect: '/jobs'},
    // /jobs/draft was the stealth URL for the pitch page pre-launch; redirect, don't 404.
    {path: '/jobs/draft', redirect: '/jobs'},
    // Community Lead listing (oxjob #662/#812).
    {path: '/jobs/community-lead', name: 'JobsCommunityLead', component: () => import('@/views/JobsCommunityLeadPage.vue'), meta: {chrome: 'site'}},
    // Software Engineer listing removed 2026-11-02 once the role was filled (oxjob #1945); LinkedIn and
    // Hacker News posts still link here. The old slug was "Software & Data Engineer" until 2026-08-21.
    {path: '/jobs/software-engineer', redirect: '/jobs'},
    {path: '/jobs/software-data-engineer', redirect: '/jobs'},
    // Operations Associate listing (oxjob #812, added 2026-08-18).
    {path: '/jobs/operations-associate', name: 'JobsOperationsAssociate', component: () => import('@/views/JobsOperationsAssociatePage.vue'), meta: {chrome: 'site'}},
    {path: '/institutional-supporters', name: 'InstitutionalSupporters', component: MembersPage, meta: {chrome: 'site'}},
    {path: '/members', redirect: '/institutional-supporters'},
    {path: '/pricing-new', redirect: '/pricing'},
    {path: '/pricing-new/institutions', redirect: '/pricing#subscriptions'},
    {path: '/pricing-new/developers', redirect: '/pricing#subscriptions'},
    {path: '/policies', redirect: '/terms'},
    {path: '/accessibility', name: 'Accessibility', component: () => import('@/views/Accessibility.vue'), meta: {chrome: 'site'}},
    {path: '/users', redirect: {name: "testimonials"}},
    {path: '/testimonials', name: "testimonials", component: TestimonialsPage, meta: {chrome: 'site'}},
    // /stats + /works-citing-openalex DELETED in oxjob #778 review (2026-08-15):
    // orphan pages Jason chose to remove rather than link from the footer.
    {path: '/events', name: 'Events', component: () => import('@/views/Events.vue'), meta: {chrome: 'site'}},
    {path: '/events/funders2026', name: 'Funders2026', component: Funders2026Page, meta: {chrome: 'site'}},
    {path: '/events/paris2026', name: 'Paris2026', component: Paris2026Page, meta: {chrome: 'site'}},
    // /discover (vector search, né /find) DELETED in oxjob #778 (2026-08-13): its
    // backing API (api.openalex.org/discover/works/*) was gone — the page had been
    // silently broken in prod — and nothing linked to it.

    // Admin Pages
    {
        path: '/admin',
        component: AdminBase,
        meta: { requiresAuth: true, requiresAdmin: true },
        children: [
            {
                path: '',
                name: 'admin-home',
                redirect: '/admin/users',
            },
            {
                path: 'users',
                name: 'admin-users',
                component: AdminUsers,
            },
            {
                path: 'users/:userId',
                name: 'admin-user-detail',
                component: AdminUserDetail,
                props: true,
            },
            {
                path: 'organizations',
                name: 'admin-organizations',
                component: AdminOrganizations,
            },
            {
                path: 'organizations/:orgId',
                name: 'admin-organization-detail',
                component: AdminOrganizationDetail,
                props: true,
            },
            {
                path: 'organizations/:orgId/members',
                name: 'admin-organization-members',
                component: AdminOrganizationMembers,
                props: true,
            },
            {
                path: 'plans',
                name: 'admin-plans',
                component: AdminPlans,
            },
            {
                path: 'affiliations',
                name: 'admin-affiliations',
                component: AdminAffiliations,
            },
            {
                path: 'hiring',
                name: 'admin-hiring',
                component: AdminHiring,
            },
            {
                path: 'hiring/:applicationId',
                name: 'admin-hiring-detail',
                component: () => import('@/views/Admin/AdminHiringDetail.vue'),
                props: true,
            },
            {
                path: 'curations',
                name: 'admin-curations',
                component: AdminCurations,
            },
            {
                path: 'curations/:curationId',
                name: 'admin-curation-detail',
                component: () => import('@/views/Admin/AdminCurationDetail.vue'),
                props: true,
            },
            {
                path: 'author-claims',
                name: 'admin-author-claims',
                component: () => import('@/views/Admin/AdminAuthorClaims.vue'),
            },
            {
                path: 'feature-flags',
                name: 'admin-feature-flags',
                component: AdminFeatureFlags,
            },
            {
                path: 'collections',
                name: 'admin-collections',
                component: AdminCollections,
            },
        ]
    },

    // Collections rename (oxjob #228 Phase 10): old `/settings/labels` and
    // `/admin/labels` redirects so stale browser bookmarks / shared links
    // don't dead-end on the 404 page. No equivalent redirect for the old
    // `/labels/<label-...>` detail-page URLs — the id format changed
    // (`label-*` → `col_*`) and the legacy data wasn't migrated, so there's
    // no stable destination. Filed as QA-053 in oxjob #228.
    {
        path: '/settings/labels',
        redirect: '/settings/collections',
    },
    {
        path: '/admin/labels',
        redirect: '/admin/collections',
    },

    // Organization Dashboard - redirect to unified settings
    {
        path: '/organizations/:orgId',
        redirect: '/settings/org-profile',
    },
    {
        path: '/organizations/:orgId/about',
        redirect: '/settings/org-profile',
    },
    {
        path: '/organizations/:orgId/members',
        redirect: '/settings/org-members',
    },

    // The corrections service and its Unpaywall Curation pages were retired (#1479); fixes go
    // through the help site until curations cover open-access links (#1483).
    redirect('/curate', "https://help.openalex.org/how-to/fixing-errors/"),
    redirect('/curate/:rest(.*)', "https://help.openalex.org/how-to/fixing-errors/"),
    
    // Docs
    redirect('/data-dump', "https://help.openalex.org/access/snapshot/"),
    redirect('/rest-api', "https://developers.openalex.org/how-to-use-the-api/api-overview"),
    redirect('/schema', "https://help.openalex.org/access/snapshot/"),
    redirect('/mag-migration-guide', "https://help.openalex.org/access/snapshot/"),
    
    // Curation requests (the Google forms these pointed at were retired in #811)
    redirect('/author-change-request', "https://help.openalex.org/how-to/fixing-errors/"),
    redirect('/authorChangeRequest', "https://help.openalex.org/how-to/fixing-errors/"),
    
    // Events (formerly help-center articles; migrated to /events in #685)
    {path: '/webinars', redirect: '/events'},
    {path: '/open-houses', redirect: '/events'},
    {path: '/user-meeting', redirect: '/events'},
    
    // Support
    redirect('/help', "https://help.openalex.org"),
    // Ticket intake moved on-site in #751 — these three used to redirect to
    // openalex.zendesk.com/hc/requests/new, which was our only intake path and
    // therefore the blocker on retiring the legacy Zendesk help center (#811).
    {path: '/support', name: 'Support', component: () => import('@/views/SupportPage.vue'), meta: {chrome: 'site'}},
    {path: '/contact', redirect: '/support'},
    {path: '/feedback', redirect: '/support'},
    
    // Resources
    redirect('/webinars/api-notebook-01', "https://github.com/ourresearch/openalex-api-tutorials/blob/main/notebooks/getting-started/api-webinar-apr2024/tutorial01.ipynb"),
    
    {path: '/:pathMatch(.*)*', name: "PageNotFound", component: PageNotFound, meta: {chrome: 'site'}},
];


// oxjob #1689 Nuxt trial: the routes, scrollBehavior and guards are exported so
// Nuxt can build its own (per-request on the server) router from them
// (app/router.options.ts, app/plugins/02.router-guards.ts). The default export is
// a late-bound proxy to whichever router instance is current, so url.js, filters.js
// and friends keep their `import router from '@/router'`.
export function scrollBehavior(to, from, savedPosition) {
        if (to.hash) {
            return {
                el: to.hash,
                top: 75
            };
        } else if (savedPosition) {
            return savedPosition;
        } else if (to.name === "Serp") {
            // do nothing
            return false;
        } else {
            return {left: 0, top: 0};
        }
}

// #397: normalize a shared/legacy/API-style search URL that carries the query in a single
// `filter=<scope>.search:<terms>` clause to the canonical `?search.*=` shape the search box
// hydrates, so it no longer lands on an empty-looking box + "No filters applied". Only the
// single, non-negated clause case is rewritten (see url.filterSearchRedirectQuery).
// Returns the redirect, or null to carry on (#1689: no next() in vue-router 5).
const redirectFilterSearchToTopLevel = function (to) {
    if (to.name !== "Serp") return null
    const newQuery = url.filterSearchRedirectQuery(to.query)
    if (!newQuery) return null
    return {...to, query: newQuery, replace: true}
}

// Entity-route fixups that used to be per-route beforeEnter guards (#1689). Nuxt
// replays the first URL once the app is created (force: true) so the global guards
// below run on it; vue-router skips beforeEnter when the replay lands on the same
// route record, which undid these on a fresh load (/locations/... rendered "Page
// not found"). As part of the global guard they run on every navigation.
// Returns a redirect, or null after any in-place fix.
const entityRouteFixup = function (to) {
    if (to.name === 'LocationPage') {
        to.params.entityType = 'locations';
        return null;
    }
    if (to.name !== 'EntityPage') return null;
    // A namespaced id in the param (/keywords/keywords%2Fkelp) means an old
    // link minted before oxjob #671's autocomplete fix — normalize it to the
    // canonical route instead of 404ing.
    const { entityId } = to.params;
    if (entityId.includes('/')) {
        const parsed = openalexId.parseId(entityId);
        if (parsed) {
            return {
                name: 'EntityPage',
                params: { entityType: parsed.entityType, entityId: parsed.shortId },
                query: to.query,
                replace: true,
            };
        }
    }
    return null;
}

export function installGuards(router, store) {
// The one live fetchUser started by the guard below (deduped across the
// redirect-and-rerun cases of beforeEach).
let liveUserBoot = null

// #1689: returns its result (a redirect, or nothing to carry on); vue-router 5
// deprecates the next() callback.
router.beforeEach(async (to) => {

    const entityRedirect = entityRouteFixup(to);
    if (entityRedirect) return entityRedirect;

    // Handle legacy v2 parameter - convert to data-version=2
    if (to.query.v2 !== undefined) {
        const newQuery = { ...to.query };
        delete newQuery.v2;
        newQuery['data-version'] = '2';
        return {
            ...to,
            query: newQuery,
            replace: true
        };
    }

    // Identity at boot (oxjob #860 round 2). With a token but no live user yet:
    // restore the cached /users/me synchronously (avatar, flags, plan), start
    // the live fetch once, and only WAIT for it where the route needs it —
    // role-gated routes always, requiresAuth routes only if the user is still
    // unknown, public routes never. App.vue paints as soon as this guard
    // resolves, so on public routes the shell no longer waits on users-api.
    // #1689: while hydrating server HTML the app must stay logged out (the server
    // can't see localStorage); the Nuxt router plugin runs this boot after hydration.
    if (hasBrowser && !isHydrating() && localStorage.getItem("token") && !store.getters["user/userLive"]) {
        if (!store.getters["user/userId"]) {
            store.dispatch("user/restoreUserFromCache")
        }
        if (!liveUserBoot) {
            liveUserBoot = store.dispatch("user/fetchUser")
                .catch((e) => {
                    // Only logout on 401 Unauthorized - transient errors
                    // (network, 5xx) should not log users out
                    if (e?.response?.status === 401) {
                        store.commit("user/logout")
                    }
                })
                .finally(() => { liveUserBoot = null })
        }
        if (shouldAwaitLiveUser({ matched: to.matched, haveUser: !!store.getters["user/userId"] })) {
            await liveUserBoot
        }
    }

    const filterSearchRedirect = redirectFilterSearchToTopLevel(to);
    if (filterSearchRedirect) return filterSearchRedirect;


    // Enforce authentication for protected routes
    if (to.matched.some(record => record.meta.requiresAuth) && !store.getters["user/userId"]) {
        return {
            name: 'Login',
            query: { redirect: to.fullPath }
        };
    }

    // Enforce admin access for admin routes
    if (to.matched.some(record => record.meta.requiresAdmin) && !store.getters["user/isAdmin"]) {
        return { name: 'Home' };
    }

    // Enforce org owner access for org owner routes
    if (to.matched.some(record => record.meta.requiresOrgOwner) && !store.getters["user/isOrgOwner"]) {
        return { name: 'settings-profile' };
    }

    // Enforce site-wide access (admin or site curator)
    if (to.matched.some(record => record.meta.requiresSiteWideAccess) && !store.getters["user/hasSiteWideAccess"]) {
        return { name: 'settings-profile' };
    }

    // Enforce org curator or owner access
    if (to.matched.some(record => record.meta.requiresOrgCuratorOrOwner)) {
        const role = store.state.user.organizationRole;
        if (!['owner', 'curator'].includes(role)) {
            return { name: 'settings-profile' };
        }
    }
});
}

let _router = null;
let _resolver = null;
export function setAppRouter(r) { _router = r; }
// Server side (Nuxt SSR), a resolver returns the current request's router.
export function setRouterResolver(fn) { _resolver = fn; }
export function getAppRouter() {
    const r = (_resolver && _resolver()) || _router;
    if (!r) throw new Error("router used before it was created");
    return r;
}
// Vue CLI entry (src/main.js): one browser router.
export function createAppRouter() {
    const r = createRouter({ history: createWebHistory(), routes, scrollBehavior });
    installGuards(r, store);
    setAppRouter(r);
    return r;
}

const router = new Proxy({}, {
    get(_t, prop) {
        const r = getAppRouter();
        const v = r[prop];
        return typeof v === 'function' ? v.bind(r) : v;
    },
});
export default router;